import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { SessionContextService } from '../../core/services/session-context.service';
import { accessDeniedReporter } from '../../core/services/activity-tracker.service';

/** Guards the full /admin tree — this sits on the `/admin` parent route, so it
 *  covers every /admin/* child.
 *
 *  Rule: `admin === 1 && !suppress_admin` (SessionContextService.canAccessAdmin,
 *  read from the `beacon_dev_user` localStorage key). Everyone else → /dashboard.
 *
 *  Two former escape hatches are gone on purpose:
 *   · "no session → pass" is unnecessary now that the check reads the
 *     synchronously-rehydrated dev user rather than the async session context,
 *     and it let anyone reach /admin during the load window.
 *   · the old `hasCommissionAccess()` (admin === 1 OR user_group === 3) let
 *     commission users without admin=1 into the tree, which the rule above
 *     disallows. That predicate has since been split into
 *     `canManageCommissions()` (the /admin CRUD gate) and
 *     `canViewCommissions()` (the profile-flyout tab gate); neither is used
 *     here, and this guard remains strictly stricter than both. */
export const beaconAdminGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
  const ctx = inject(SessionContextService);
  const router = inject(Router);

  if (ctx.canAccessAdmin()) return true;

  accessDeniedReporter('admin')(state.url);
  router.navigate(['/dashboard']);
  return false;
};

/** Beacon-platform-only routes (companies list, cross-company reports,
 *  usage). Customer Super Admins (admin=1 at non-Beacon company) hit
 *  this guard and get bounced to their own Super Admin home. */
export const beaconGodAdminGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
  const ctx = inject(SessionContextService);
  const router = inject(Router);
  if (!ctx.context()) return true;
  if (ctx.isBeaconAdmin()) return true;
  accessDeniedReporter('beacon-admin')(state.url);
  router.navigate(['/admin/home']);
  return false;
};

/** Per-company admin pages (config / users / permissions / shelves /
 *  spreadsheets / reports). Beacon god admins see any company; everyone
 *  else can only access their own company's admin pages. Reads the
 *  ':id' param from the route. */
export const companyAdminGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const ctx = inject(SessionContextService);
  const router = inject(Router);
  if (!ctx.context()) return true;
  if (ctx.isBeaconAdmin()) return true;

  const targetId = Number(route.paramMap.get('id'));
  const userCompanyId = ctx.context()?.user?.user_company_id;
  if (targetId && userCompanyId === targetId) return true;

  accessDeniedReporter('company-admin')(state.url);
  router.navigate(['/admin/home']);
  return false;
};
